import React, { useState } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  message, 
  Typography,
  Divider,
  Space,
  Table,
  Modal,
  Tag,
  Select,
  Popconfirm,
  TimePicker,
  Tooltip,
  Alert,
  InputNumber,
  Switch
} from 'antd';
import {
  SaveOutlined,
  DeleteOutlined,
  PlusOutlined,
  EditOutlined,
  KeyOutlined,
  ApiOutlined,
  ClockCircleOutlined,
  PlayCircleOutlined,
  SyncOutlined,
  HeartOutlined,
  WarningOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Password } = Input;

const Settings = () => {
  const [configForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [industryForm] = Form.useForm();
  const [healthCheckForm] = Form.useForm();
  const [isIndustryModalVisible, setIsIndustryModalVisible] = useState(false);
  const [editingIndustry, setEditingIndustry] = useState(null);
  const [testingOpenAI, setTestingOpenAI] = useState(false);
  const [newsImportTime, setNewsImportTime] = useState(null);
  const [triggeringImport, setTriggeringImport] = useState(false);
  const [importHoursLimit, setImportHoursLimit] = useState(26);
  const [healthCheckTime, setHealthCheckTime] = useState(null);
  const [triggeringHealthCheck, setTriggeringHealthCheck] = useState(false);
  const { user, changePassword } = useAuth();
  const queryClient = useQueryClient();

  // 获取配置
  const { data: config = {}, isLoading: configLoading } = useQuery(
    'config',
    () => axios.get('/api/config').then(res => res.data)
  );

  // 获取行业列表
  const { data: industries = [], isLoading: industriesLoading } = useQuery(
    'industries',
    () => axios.get('/api/config/industries').then(res => res.data).catch(() => [])
  );

  // 获取新闻导入时间配置
  const { data: newsImportSchedule, isLoading: scheduleLoading, isError: scheduleError } = useQuery(
    'newsImportSchedule',
    () => axios.get('/api/config/news-import-schedule').then(res => res.data),
    {
      retry: 1,
      onSuccess: (data) => {
        // 解析cron表达式获取时间 (格式: 分 时 * * *)
        const schedule = data?.schedule || data?.default;
        if (schedule) {
          const parts = schedule.split(' ');
          if (parts.length >= 2) {
            const minute = parseInt(parts[0]);
            const hour = parseInt(parts[1]);
            if (!isNaN(minute) && !isNaN(hour)) {
              setNewsImportTime(dayjs().hour(hour).minute(minute));
            }
          }
        }
      }
    }
  );

  // 获取新闻源健康检查配置
  const { data: healthCheckConfig, isLoading: healthCheckLoading } = useQuery(
    'healthCheckConfig',
    () => axios.get('/api/config/news-health-check').then(res => res.data),
    {
      retry: 1,
      onSuccess: (data) => {
        if (data?.checkTime) {
          const [hour, minute] = data.checkTime.split(':').map(Number);
          setHealthCheckTime(dayjs().hour(hour).minute(minute));
        }
        healthCheckForm.setFieldsValue({
          enabled: data?.enabled ?? true,
          checkIntervalHours: data?.checkIntervalHours ?? 24,
          alertEmail: data?.alertEmail ?? 'tom.gu@decentcapital.com'
        });
      }
    }
  );

  // 获取新闻源健康状态
  const { data: healthStatus, isLoading: healthStatusLoading, refetch: refetchHealthStatus } = useQuery(
    'newsSourceHealth',
    () => axios.get('/api/config/news-source-health', { params: { hours: healthCheckConfig?.checkIntervalHours || 24 } }).then(res => res.data),
    {
      enabled: !!healthCheckConfig,
      refetchOnWindowFocus: false
    }
  );

  // 保存配置
  const saveConfigMutation = useMutation(
    (data) => axios.post('/api/config/batch', { configs: data }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('config');
        message.success('配置保存成功');
      },
      onError: (error) => {
        message.error(error.response?.data?.error || '保存失败');
      }
    }
  );

  // 修改密码
  const changePasswordMutation = useMutation(
    (data) => changePassword(data.currentPassword, data.newPassword),
    {
      onSuccess: (result) => {
        if (result.success) {
          message.success('密码修改成功');
          passwordForm.resetFields();
        } else {
          message.error(result.message);
        }
      }
    }
  );

  // 添加/更新行业
  const saveIndustryMutation = useMutation(
    (data) => axios.post('/api/config/industry', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('industries');
        message.success('行业配置保存成功');
        setIsIndustryModalVisible(false);
        setEditingIndustry(null);
        industryForm.resetFields();
      },
      onError: (error) => {
        message.error(error.response?.data?.error || '保存失败');
      }
    }
  );

  // 删除行业
  const deleteIndustryMutation = useMutation(
    (id) => axios.delete(`/api/config/industry/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('industries');
        message.success('行业删除成功');
      },
      onError: (error) => {
        message.error(error.response?.data?.error || '删除失败');
      }
    }
  );

  // 保存新闻导入时间
  const saveNewsImportScheduleMutation = useMutation(
    (schedule) => axios.post('/api/config/news-import-schedule', { schedule }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('newsImportSchedule');
        message.success('新闻导入时间保存成功');
      },
      onError: (error) => {
        message.error(error.response?.data?.error || '保存失败');
      }
    }
  );

  // 手动触发新闻导入
  const triggerNewsImportMutation = useMutation(
    (hoursLimit) => axios.post('/api/config/trigger-news-import', { hoursLimit }),
    {
      onSuccess: (res) => {
        message.success(`新闻导入已启动（${res.data.hoursLimit}小时内），请稍后查看结果`);
      },
      onError: (error) => {
        message.error(error.response?.data?.error || '触发失败');
      }
    }
  );

  // 保存健康检查配置
  const saveHealthCheckMutation = useMutation(
    (config) => axios.post('/api/config/news-health-check', config),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('healthCheckConfig');
        message.success('健康检查配置保存成功');
      },
      onError: (error) => {
        message.error(error.response?.data?.error || '保存失败');
      }
    }
  );

  // 手动触发健康检查
  const triggerHealthCheckMutation = useMutation(
    () => axios.post('/api/config/trigger-health-check'),
    {
      onSuccess: () => {
        message.success('健康检查已启动，如有异常将发送告警邮件');
        setTimeout(() => {
          refetchHealthStatus();
        }, 2000);
      },
      onError: (error) => {
        message.error(error.response?.data?.error || '触发失败');
      }
    }
  );

  // 测试OpenAI API
  const testOpenAIMutation = useMutation(
    (data) => axios.post('/api/config/test-openai', data),
    {
      onSuccess: (response) => {
        message.success(`API连接成功! 模型: ${response.data.model}`);
        console.log('OpenAI Test Response:', response.data);
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || 'API测试失败';
        const errorDetails = error.response?.data?.details;
        message.error(`${errorMsg}${errorDetails ? ': ' + errorDetails : ''}`);
        console.error('OpenAI Test Error:', error.response?.data);
      }
    }
  );

  const handleSaveConfig = () => {
    configForm.validateFields().then((values) => {
      saveConfigMutation.mutate(values);
    });
  };

  const handleChangePassword = () => {
    passwordForm.validateFields().then((values) => {
      if (values.newPassword !== values.confirmPassword) {
        message.error('两次输入的密码不一致');
        return;
      }
      changePasswordMutation.mutate(values);
    });
  };

  const handleAddIndustry = () => {
    setEditingIndustry(null);
    setIsIndustryModalVisible(true);
    industryForm.resetFields();
  };

  const handleEditIndustry = (industry) => {
    setEditingIndustry(industry);
    setIsIndustryModalVisible(true);
    industryForm.setFieldsValue({
      name: industry.name,
      keywords: industry.keywords
    });
  };

  const handleSaveIndustry = () => {
    industryForm.validateFields().then((values) => {
      const data = {
        ...values,
        id: editingIndustry?.id
      };
      saveIndustryMutation.mutate(data);
    });
  };

  const handleSaveNewsImportTime = () => {
    if (!newsImportTime) {
      message.error('请选择导入时间');
      return;
    }
    const hour = newsImportTime.hour();
    const minute = newsImportTime.minute();
    // 生成cron表达式: 分 时 * * * (每天执行)
    const cronExpression = `${minute} ${hour} * * *`;
    saveNewsImportScheduleMutation.mutate(cronExpression);
  };

  const handleTriggerNewsImport = () => {
    setTriggeringImport(true);
    triggerNewsImportMutation.mutate(importHoursLimit, {
      onSettled: () => {
        setTriggeringImport(false);
      }
    });
  };

  const handleSaveHealthCheck = () => {
    healthCheckForm.validateFields().then((values) => {
      const config = {
        ...values,
        checkTime: healthCheckTime ? healthCheckTime.format('HH:mm') : '08:00'
      };
      saveHealthCheckMutation.mutate(config);
    });
  };

  const handleTriggerHealthCheck = () => {
    setTriggeringHealthCheck(true);
    triggerHealthCheckMutation.mutate(undefined, {
      onSettled: () => {
        setTriggeringHealthCheck(false);
      }
    });
  };

  const handleTestOpenAI = () => {
    configForm.validateFields(['openai_api_key', 'openai_model']).then((values) => {
      const { openai_api_key, openai_model } = values;
      if (!openai_api_key) {
        message.error('请先填写OpenAI API Key');
        return;
      }
      setTestingOpenAI(true);
      testOpenAIMutation.mutate(
        { 
          apiKey: openai_api_key, 
          model: openai_model || 'gpt-4o' 
        },
        {
          onSettled: () => {
            setTestingOpenAI(false);
          }
        }
      );
    }).catch(() => {
      message.error('请填写完整的OpenAI配置信息');
    });
  };

  const industryColumns = [
    {
      title: '行业名称',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: '关键词',
      dataIndex: 'keywords',
      key: 'keywords',
      render: (keywords) => {
        if (!keywords) return <Text type="secondary">无</Text>;
        const keywordList = keywords.split(',').map(k => k.trim());
        return (
          <div>
            {keywordList.slice(0, 3).map(keyword => (
              <Tag key={keyword} style={{ margin: '2px' }}>
                {keyword}
              </Tag>
            ))}
            {keywordList.length > 3 && (
              <Text type="secondary">+{keywordList.length - 3}</Text>
            )}
          </div>
        );
      }
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditIndustry(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个行业配置吗？"
            onConfirm={() => deleteIndustryMutation.mutate(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div className="page-header">
        <Title level={2} className="page-title">系统设置</Title>
        <Text className="page-description">
          配置系统参数、API密钥和个人偏好
        </Text>
      </div>

      {/* API配置 */}
      <Card title="API配置" style={{ marginBottom: '24px' }}>
        <Form
          form={configForm}
          layout="vertical"
          initialValues={config}
          onValuesChange={(_, allValues) => {
            // 实时保存表单值到state
          }}
        >
          <div className="config-section">
            <div className="config-section-title">LLM配置</div>
            <Form.Item
              name="openai_api_key"
              label="OpenAI API Key"
              extra="用于新闻摘要和情感分析"
            >
              <Password 
                placeholder="sk-..."
                visibilityToggle={false}
              />
            </Form.Item>

            <Form.Item
              name="openai_model"
              label="OpenAI模型"
            >
              <Select placeholder="选择模型">
                <Option value="gpt-3.5-turbo">GPT-3.5 Turbo</Option>
                <Option value="gpt-4">GPT-4</Option>
                <Option value="gpt-4-turbo">GPT-4 Turbo</Option>
                <Option value="gpt-4o">GPT-4o</Option>
                <Option value="gpt-4o-mini">GPT-4o Mini</Option>
              </Select>
            </Form.Item>

            <Form.Item>
              <Button
                type="default"
                icon={<ApiOutlined />}
                onClick={handleTestOpenAI}
                loading={testingOpenAI}
                style={{ marginBottom: '16px' }}
              >
                测试API连接
              </Button>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                点击测试按钮验证API密钥和模型配置是否正确
              </div>
            </Form.Item>
          </div>

          <Divider />

          <div className="config-section">
            <div className="config-section-title">邮件配置</div>
            <Form.Item
              name="email_host"
              label="SMTP服务器"
            >
              <Input placeholder="smtp.gmail.com" />
            </Form.Item>

            <div className="form-row">
              <Form.Item
                name="email_port"
                label="端口"
              >
                <Input placeholder="587" />
              </Form.Item>

              <Form.Item
                name="email_user"
                label="邮箱账号"
              >
                <Input placeholder="your-email@gmail.com" />
              </Form.Item>
            </div>

            <Form.Item
              name="email_password"
              label="邮箱密码"
              extra="建议使用应用专用密码"
            >
              <Password placeholder="邮箱密码或应用密码" />
            </Form.Item>

            <Form.Item
              name="email_schedule"
              label="发送时间"
              extra="Cron格式，默认工作日早上8点"
            >
              <Input placeholder="0 8 * * 1-5" />
            </Form.Item>
          </div>

          <Divider />

          <div className="config-section">
            <div className="config-section-title">新闻源配置</div>
            <Form.Item
              name="news_api_key"
              label="News API Key"
              extra="可选，用于获取更多新闻源"
            >
              <Password placeholder="News API密钥" />
            </Form.Item>

            <Form.Item
              name="custom_news_sources"
              label="自定义新闻源"
              extra="每行一个URL"
            >
              <TextArea 
                rows={4}
                placeholder="https://example.com/rss&#10;https://another-source.com/feed"
              />
            </Form.Item>
          </div>

          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveConfig}
            loading={saveConfigMutation.isLoading}
            size="large"
          >
            保存配置
          </Button>
        </Form>
      </Card>

      {/* 新闻导入定时任务配置 */}
      <Card 
        title={
          <Space>
            <ClockCircleOutlined />
            新闻导入定时任务
          </Space>
        }
        style={{ marginBottom: '24px' }}
      >
        <Alert
          message="每天自动从腾讯云数据库导入最新新闻"
          description="系统会自动导入26小时内的新闻，并排除已存在的重复新闻。"
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ marginBottom: '8px', fontWeight: 500 }}>导入时间</div>
            <Space>
              <TimePicker
                value={newsImportTime}
                onChange={setNewsImportTime}
                format="HH:mm"
                placeholder="选择时间"
                style={{ width: '120px' }}
                disabled={scheduleLoading}
              />
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveNewsImportTime}
                loading={saveNewsImportScheduleMutation.isLoading}
              >
                保存
              </Button>
            </Space>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
              当前配置: {scheduleLoading ? '加载中...' : scheduleError ? '获取失败，请重启服务器' : (newsImportSchedule?.schedule || newsImportSchedule?.default || '未配置')}
            </div>
          </div>
          
          <Divider type="vertical" style={{ height: '60px' }} />
          
          <div>
            <div style={{ marginBottom: '8px', fontWeight: 500 }}>手动导入</div>
            <Space>
              <InputNumber
                min={1}
                max={168}
                value={importHoursLimit}
                onChange={setImportHoursLimit}
                addonAfter="小时"
                style={{ width: '120px' }}
              />
              <Tooltip title={`导入${importHoursLimit}小时内的新闻`}>
                <Button
                  icon={triggeringImport ? <SyncOutlined spin /> : <PlayCircleOutlined />}
                  onClick={handleTriggerNewsImport}
                  loading={triggeringImport}
                >
                  立即导入
                </Button>
              </Tooltip>
            </Space>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
              设置导入时间范围后点击导入（最大168小时/7天）
            </div>
          </div>
        </div>
      </Card>

      {/* 新闻源健康检查配置 */}
      <Card 
        title={
          <Space>
            <HeartOutlined />
            新闻源健康检查
          </Space>
        }
        style={{ marginBottom: '24px' }}
      >
        <Alert
          message="定时检查新闻源爬虫状态"
          description={
            <div>
              <p style={{ margin: 0 }}>系统会从腾讯云 news 数据库检查以下新闻源在指定时间范围内是否有新闻（content不为空）。如果某个新闻源的新闻数量为0，说明该新闻源的爬虫可能出现了问题，系统将发送告警邮件。</p>
              <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                <strong>监控的新闻源：</strong>Wall Street CN, Wall Street Journal, Bloomberg, WallStreet资讯, Financial Times, TechCrunch, The Information, ETNews, The Elec, MK, EE Times Japan, 經濟日報
              </p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        
        <Form
          form={healthCheckForm}
          layout="vertical"
          initialValues={{
            enabled: true,
            checkIntervalHours: 24,
            alertEmail: 'tom.gu@decentcapital.com'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1', minWidth: '300px' }}>
              <Form.Item
                name="enabled"
                label="启用健康检查"
                valuePropName="checked"
              >
                <Switch checkedChildren="启用" unCheckedChildren="禁用" />
              </Form.Item>
              
              <Form.Item
                name="checkIntervalHours"
                label="检查时间范围（小时）"
                extra="检查最近多少小时内的新闻数量"
              >
                <InputNumber 
                  min={1} 
                  max={168} 
                  style={{ width: '120px' }}
                  addonAfter="小时"
                />
              </Form.Item>
              
              <div style={{ marginBottom: '16px' }}>
                <div style={{ marginBottom: '8px', fontWeight: 500 }}>检查时间</div>
                <Space>
                  <TimePicker
                    value={healthCheckTime}
                    onChange={setHealthCheckTime}
                    format="HH:mm"
                    placeholder="选择时间"
                    style={{ width: '120px' }}
                    disabled={healthCheckLoading}
                  />
                </Space>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                  每天在此时间执行健康检查
                </div>
              </div>
              
              <Form.Item
                name="alertEmail"
                label="告警邮箱"
                extra="当检测到异常时发送告警邮件到此邮箱"
                rules={[
                  { type: 'email', message: '请输入有效的邮箱地址' }
                ]}
              >
                <Input placeholder="tom.gu@decentcapital.com" style={{ maxWidth: '300px' }} />
              </Form.Item>
              
              <Space>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSaveHealthCheck}
                  loading={saveHealthCheckMutation.isLoading}
                >
                  保存配置
                </Button>
                <Tooltip title="立即执行一次健康检查">
                  <Button
                    icon={triggeringHealthCheck ? <SyncOutlined spin /> : <PlayCircleOutlined />}
                    onClick={handleTriggerHealthCheck}
                    loading={triggeringHealthCheck}
                  >
                    立即检查
                  </Button>
                </Tooltip>
              </Space>
            </div>
            
            <div style={{ flex: '1', minWidth: '300px' }}>
              <div style={{ marginBottom: '8px', fontWeight: 500 }}>
                当前健康状态
                <Button 
                  type="link" 
                  size="small" 
                  icon={<SyncOutlined />}
                  onClick={() => refetchHealthStatus()}
                  loading={healthStatusLoading}
                >
                  刷新
                </Button>
              </div>
              
              {healthStatusLoading ? (
                <div style={{ color: '#999' }}>加载中...</div>
              ) : healthStatus ? (
                <div style={{ 
                  background: '#f8f9fa', 
                  padding: '16px', 
                  borderRadius: '8px',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <Tag color="blue">总计: {healthStatus.totalSources} 个新闻源</Tag>
                    <Tag color="green">正常: {healthStatus.healthySources?.length || 0}</Tag>
                    <Tag color="red">异常: {healthStatus.unhealthySources?.length || 0}</Tag>
                  </div>
                  
                  {healthStatus.unhealthySources?.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontWeight: 500, color: '#e74c3c', marginBottom: '8px' }}>
                        <WarningOutlined /> 异常新闻源（{healthStatus.hoursChecked}小时内无新闻）
                      </div>
                      {healthStatus.unhealthySources.map(source => (
                        <Tag key={source.id} color="error" style={{ margin: '2px' }}>
                          {source.name}
                        </Tag>
                      ))}
                    </div>
                  )}
                  
                  {healthStatus.healthySources?.length > 0 && (
                    <div>
                      <div style={{ fontWeight: 500, color: '#27ae60', marginBottom: '8px' }}>
                        <CheckCircleOutlined /> 正常新闻源
                      </div>
                      {healthStatus.healthySources.slice(0, 10).map(source => (
                        <Tooltip key={source.id} title={`${source.newsCount} 条新闻`}>
                          <Tag color="success" style={{ margin: '2px' }}>
                            {source.name} ({source.newsCount})
                          </Tag>
                        </Tooltip>
                      ))}
                      {healthStatus.healthySources.length > 10 && (
                        <Tag style={{ margin: '2px' }}>+{healthStatus.healthySources.length - 10} 更多</Tag>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: '#999' }}>暂无数据</div>
              )}
            </div>
          </div>
        </Form>
      </Card>

      {/* 行业关注配置 */}
      <Card 
        title="关注行业"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddIndustry}
          >
            添加行业
          </Button>
        }
        style={{ marginBottom: '24px' }}
      >
        <Table
          columns={industryColumns}
          dataSource={industries}
          rowKey="id"
          loading={industriesLoading}
          pagination={false}
          locale={{ emptyText: '暂无行业配置，点击添加行业开始关注特定行业新闻' }}
        />
      </Card>

      {/* 账户安全 */}
      <Card title="账户安全">
        <div style={{ marginBottom: '16px' }}>
          <Text strong>当前账户：</Text>
          <Text>{user?.email}</Text>
          {user?.role === 'admin' && (
            <Tag color="blue" style={{ marginLeft: '8px' }}>管理员</Tag>
          )}
        </div>

        <Divider />

        <div className="config-section-title">修改密码</div>
        <Form
          form={passwordForm}
          layout="vertical"
          style={{ maxWidth: '400px' }}
        >
          <Form.Item
            name="currentPassword"
            label="当前密码"
            rules={[{ required: true, message: '请输入当前密码' }]}
          >
            <Password placeholder="输入当前密码" />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6位' }
            ]}
          >
            <Password placeholder="输入新密码" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            rules={[{ required: true, message: '请确认新密码' }]}
          >
            <Password placeholder="再次输入新密码" />
          </Form.Item>

          <Button
            type="primary"
            icon={<KeyOutlined />}
            onClick={handleChangePassword}
            loading={changePasswordMutation.isLoading}
          >
            修改密码
          </Button>
        </Form>
      </Card>

      {/* 行业配置弹窗 */}
      <Modal
        title={editingIndustry ? '编辑行业' : '添加行业'}
        open={isIndustryModalVisible}
        onOk={handleSaveIndustry}
        onCancel={() => {
          setIsIndustryModalVisible(false);
          setEditingIndustry(null);
          industryForm.resetFields();
        }}
        confirmLoading={saveIndustryMutation.isLoading}
      >
        <Form
          form={industryForm}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="行业名称"
            rules={[{ required: true, message: '请输入行业名称' }]}
          >
            <Input placeholder="如：科技、金融、医疗" />
          </Form.Item>

          <Form.Item
            name="keywords"
            label="关键词"
            extra="用逗号分隔多个关键词，用于筛选相关新闻"
            rules={[{ required: true, message: '请输入关键词' }]}
          >
            <TextArea 
              rows={3}
              placeholder="人工智能,机器学习,云计算,大数据"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Settings;
